import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useEditorURL, type PlainEditor } from "@/queries/editor-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { type PlainMessage } from "@bufbuild/protobuf";
import { type Environment } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback } from "react";

export function useOpenEditor() {
    const editorURL = useEditorURL();
    const segmentTrack = useSegmentTrack();
    const { data: organizationPolicies } = useGetOrganizationPolicies();
    const { toast } = useToast();

    return useCallback(
        async (editor: PlainEditor, environment?: Environment | PlainMessage<Environment>) => {
            try {
                if (!organizationPolicies) {
                    return;
                }

                if(organizationPolicies.portSharingDisabled && editor.alias === "vscode-browser") {
                    toast({
                        title: "Port sharing required for VS Code Browser",
                        description: "VS Code Browser requires port sharing to be enabled in your organization policies.",
                    })
                    return;
                }

                const result = await editorURL.mutateAsync({ editorId: editor.id, environment });
                const url = result.url;
                if (!url) {
                    return;
                }

                const redirect = !/^https?:\/\//.test(url);
                // redirect only if points to desktop application
                // don't navigate browser to another page
                segmentTrack("Open Editor", {
                    redirect,
                    editor_id: editor.id,
                    editor_alias: editor.alias,
                    editor_name: editor.name,
                });
                if (redirect) {
                    window.location.href = url;
                } else {
                    window.open(url, "_blank", "noopener");
                }
            } catch (e) {
                console.error(e);
            }
        },
        [editorURL, segmentTrack, organizationPolicies, toast],
    );
}
